import { createContext, useContext, useMemo } from 'react';
import type { ImageBleed } from 'types';
import type { ModuleRowColumn, ModuleRowModule } from 'types/capi/article';

import BrandedHeading from '~/components/Content/BrandedHeading';
import BrandedQuote from '~/components/Content/BrandedQuote';
import ContentBlock from '~/modules/ste/ContentBlock';
import EmbedBlock from '~/modules/ste/EmbedBlock';
import EmbeddedBrandedHeading from '~/modules/ste/embedded/EmbeddedBrandedHeading';
import EmbeddedBrandedQuote from '~/modules/ste/embedded/EmbeddedBrandedQuote';
import EmbeddedContentBlock from '~/modules/ste/embedded/EmbeddedContentBlock';
import EmbeddedEmbedBlock from '~/modules/ste/embedded/EmbeddedEmbedBlock';
import EmbeddedImageGrid from '~/modules/ste/embedded/EmbeddedImageGrid';
import ImageGrid from '~/modules/ste/ImageGrid';
import ProductList from '~/modules/ste/ProductList';

type ModuleRowContextValue = {
  narrow: boolean;
};

const ModuleRowContext = createContext<ModuleRowContextValue | null>(null);

export function useModuleRowContext() {
  return useContext(ModuleRowContext);
}

type Props = {
  module: ModuleRowModule;
};

type ColumnModule = ModuleRowColumn['modules'][number];

export type ColumnPosition = 'first' | 'last' | 'middle';

const containerStyle = { containerType: 'inline-size' } as const;

const gridByColumnCount: Record<number, string> = {
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4'
};

export default function ModuleRow({ module }: Props) {
  const { columns } = module;
  const narrow = (columns?.length ?? 0) >= 3;
  const contextValue = useMemo(() => ({ narrow }), [narrow]);

  if (!columns?.length) return null;

  if (columns.length === 1) {
    const column = columns[0]!;
    const columnHidden = isColumnHiddenOnMobile(column);

    return column.modules.map((childModule, modIndex) => (
      <BaseModule
        key={modIndex}
        module={childModule}
        hiddenOnMobile={columnHidden || !!childModule.mobile_hidden}
      />
    ));
  }

  const gridCols =
    gridByColumnCount[columns.length] ?? 'grid-cols-1 md:grid-cols-2';
  const allColumnsHiddenOnMobile = columns.every(isColumnHiddenOnMobile);
  const wrapperClasses = `flex w-full items-start justify-center max-md:px-4 max-md:pb-4 ${
    allColumnsHiddenOnMobile ? 'max-md:hidden' : ''
  }`;

  return (
    <ModuleRowContext.Provider value={contextValue}>
      <div className={wrapperClasses} style={containerStyle}>
        <div
          className={`grid w-full max-w-desktop gap-x-4 gap-y-6 md:gap-6 ${gridCols}`}
        >
          {columns.map((column, colIndex) => (
            <Column
              key={colIndex}
              column={column}
              index={colIndex}
              numColumns={columns.length}
            />
          ))}
        </div>
      </div>
    </ModuleRowContext.Provider>
  );
}

function BaseModule({
  hiddenOnMobile,
  module
}: {
  hiddenOnMobile?: boolean;
  module: ColumnModule;
}) {
  const inner = renderBaseModule(module);
  if (!inner) return null;
  if (!hiddenOnMobile) return inner;

  return <div className="max-md:hidden">{inner}</div>;
}

function renderBaseModule(module: ColumnModule) {
  switch (module.type) {
    case 'branded-heading':
      return <BrandedHeading module={module} />;
    case 'branded-quote':
      return <BrandedQuote quote={module} />;
    case 'content-block':
      if (!module.content?.length) return null;

      return <ContentBlock {...module} />;
    case 'embed':
      return <EmbedBlock embed={module} />;
    case 'image-grid':
      return <ImageGrid module={module} />;
    case 'product-list':
      return <ProductList {...module} pageType="article" />;
    default:
      return null;
  }
}

const contentBlockCollapseTypes: ColumnModule['type'][] = [
  'branded-heading',
  'branded-quote'
];

const bleedBaseClasses =
  'md:[--bleed:max(0px,calc(50cqw-35rem))] md:w-[calc(100%+var(--bleed))]';

function getColumnBleed(
  hasBleed: boolean,
  position: ColumnPosition
): ImageBleed {
  if (!hasBleed) return 'none';
  if (position === 'first') return 'left';
  if (position === 'last') return 'right';

  return 'none';
}

export function getBleedClass(bleed: ImageBleed, numColumns: number) {
  if (bleed === 'none') return '';

  if (bleed === 'left')
    return `${bleedBaseClasses} md:ml-[calc(var(--bleed)*-1)] ${
      numColumns > 2 ? 'lg:pr-[94px]' : ''
    }`;

  if (bleed === 'right')
    return `${bleedBaseClasses} ${numColumns > 2 ? 'lg:pl-[94px]' : ''}`;

  return '';
}

function isColumnHiddenOnMobile(column: ModuleRowColumn) {
  if (column.mobile_hidden) return true;
  if (!column.modules.length) return true;

  return column.modules.every((m) => m.mobile_hidden);
}

function Column({
  column,
  index,
  numColumns
}: {
  column: ModuleRowColumn;
  index: number;
  numColumns: number;
}) {
  const columnPosition: ColumnPosition =
    index === 0 ? 'first' : index === numColumns - 1 ? 'last' : 'middle';
  const bleed = getColumnBleed(!!column.bleed, columnPosition);
  const isHiddenOnMobile = isColumnHiddenOnMobile(column);

  return (
    <div
      className={`flex flex-col gap-6 md:sticky md:top-20 md:self-start ${
        isHiddenOnMobile ? 'max-md:hidden' : ''
      }`}
    >
      {column.modules.map((childModule, modIndex) => (
        <EmbeddedModule
          key={modIndex}
          bleed={bleed}
          columnPosition={columnPosition}
          hiddenOnMobile={!isHiddenOnMobile && !!childModule.mobile_hidden}
          module={childModule}
          nextType={column.modules[modIndex + 1]?.type}
          numColumns={numColumns}
          prevType={column.modules[modIndex - 1]?.type}
        />
      ))}
    </div>
  );
}

function EmbeddedModule({
  bleed,
  hiddenOnMobile,
  module,
  nextType,
  numColumns,
  columnPosition,
  prevType
}: {
  bleed: ImageBleed;
  columnPosition: ColumnPosition;
  hiddenOnMobile?: boolean;
  module: ColumnModule;
  nextType?: ColumnModule['type'];
  numColumns: number;
  prevType?: ColumnModule['type'];
}) {
  const inner = renderEmbeddedModule({
    bleed,
    columnPosition,
    module,
    nextType,
    numColumns,
    prevType
  });
  if (!inner) return null;
  if (!hiddenOnMobile) return inner;

  return <div className="max-md:hidden">{inner}</div>;
}

function renderEmbeddedModule({
  bleed,
  module,
  nextType,
  numColumns,
  columnPosition,
  prevType
}: {
  bleed: ImageBleed;
  columnPosition: ColumnPosition;
  module: ColumnModule;
  nextType?: ColumnModule['type'];
  numColumns: number;
  prevType?: ColumnModule['type'];
}) {
  switch (module.type) {
    case 'branded-heading':
      return (
        <EmbeddedBrandedHeading
          columnPosition={columnPosition}
          module={module}
        />
      );
    case 'branded-quote':
      return (
        <EmbeddedBrandedQuote columnPosition={columnPosition} quote={module} />
      );
    case 'content-block':
      if (!module.content?.length) return null;

      return (
        <EmbeddedContentBlock
          {...module}
          collapseBottom={
            !!nextType && contentBlockCollapseTypes.includes(nextType)
          }
          collapseTop={
            !!prevType && contentBlockCollapseTypes.includes(prevType)
          }
          columnPosition={columnPosition}
        />
      );
    case 'embed':
      return <EmbeddedEmbedBlock embed={module} />;
    case 'image-grid':
      return (
        <EmbeddedImageGrid
          module={module}
          bleed={bleed}
          numColumns={numColumns}
        />
      );
    case 'product-list':
      return <ProductList {...module} pageType="article" />;
    default:
      return null;
  }
}
